import type {
  FavoritesFragment,
  GetFavoritesQuery,
  UpdateFavoriteListMutation,
  UpdateFavoriteListMutationVariables,
  DeleteFavoriteListMutation,
  DeleteFavoriteListMutationVariables,
  NotifyRemoveFavoriteMutationVariables,
  NotifyRemoveFavoriteMutation,
  NotifyAddFavoriteMutationVariables,
  NotifyAddFavoriteMutation,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { GET_FAVORITES } from '@dustin-web/microsite-data/src/graphql/product/get-customer-product-lists.graphql'
import { UPDATE_FAVORITE_LIST } from '@dustin-web/microsite-data/src/graphql/product/update-favorite-list.graphql'
import { DELETE_FAVORITE_LIST } from '@dustin-web/microsite-data/src/graphql/product/delete-favorite-list.graphql'
import { NOTIFY_ADD_FAVORITE } from '@dustin-web/microsite-data/src/graphql/product/notify-add-favorite.graphql'
import { NOTIFY_REMOVE_FAVORITE } from '@dustin-web/microsite-data/src/graphql/product/notify-remove-favorite.graphql'
import { useGraphql, useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'
import { getUpdatedFavoritesCache } from '@dustin-web/microsite-data/src/hooks/cache-updater-favorites'
import { useCallback } from 'react'

export type UpdateFavorites = ReturnType<typeof useFavorites>['updateFavorites']
export type FavoriteProductList = NonNullable<FavoritesFragment['productLists']>

export type UseFavorites = typeof useFavorites

export const useFavorites = () => {
  const {
    data: favoritesData,
    error,
    isValidating: loading,
    mutate,
  } = useGraphql<GetFavoritesQuery>(GET_FAVORITES)

  const [updateFavoritesMutation] = useGraphqlMutation<
    UpdateFavoriteListMutation,
    UpdateFavoriteListMutationVariables
  >(UPDATE_FAVORITE_LIST)

  const [deleteFavoriteListMutation] = useGraphqlMutation<
    DeleteFavoriteListMutation,
    DeleteFavoriteListMutationVariables
  >(DELETE_FAVORITE_LIST)

  const [notifyAddFavorite] = useGraphqlMutation<
    NotifyAddFavoriteMutation,
    NotifyAddFavoriteMutationVariables
  >(NOTIFY_ADD_FAVORITE)

  const [notifyRemoveFavorite] = useGraphqlMutation<
    NotifyRemoveFavoriteMutation,
    NotifyRemoveFavoriteMutationVariables
  >(NOTIFY_REMOVE_FAVORITE)

  const notifyFavoriteClick = async (event: 'add' | 'remove', productErpIdentifier: string) => {
    await (event === 'add' ? notifyAddFavorite : notifyRemoveFavorite)({
      variables: {
        productErpIdentifier,
      },
    })
  }

  const updateFavorites = useCallback(
    ({
      productId,
      displayName,
      favoriteListName,
      quantity,
      favoriteListId,
      favoriteList,
    }: {
      productId: string
      displayName: string
      favoriteListName: string
      quantity: number
      favoriteListId?: number
      favoriteList?: GetFavoritesQuery
      formData?: FormData
    }) => {
      return mutate(
        async () => {
          const newData = await updateFavoritesMutation({
            variables: {
              favoriteListId,
              favoriteListName,
              productErpIdentifier: productId,
              quantity,
            },
          })

          const updatedProductList = getUpdatedFavoritesCache({
            productId,
            displayName,
            quantity,
            favoriteListName,
            newData,
            favoritesData: favoriteList ?? (favoritesData as GetFavoritesQuery),
          })
          const updatedData = {
            favorites: { __typename: 'Favorites', productLists: updatedProductList },
          }

          return updatedData as GetFavoritesQuery
        },
        {
          rollbackOnError: true,
          revalidate: false,
        }
      )
    },
    [favoritesData, mutate, updateFavoritesMutation]
  )

  const deleteFavoriteList = useCallback(
    (id: number) => {
      mutate(
        async () => {
          const deletedData = await deleteFavoriteListMutation({
            variables: {
              id: id,
            },
          })

          const cachedFavoritesLists = favoritesData?.favorites?.productLists ?? []
          const updatedData = {
            favorites: {
              __typename: 'Favorites',
              productLists: cachedFavoritesLists?.filter(
                list => list?.id !== deletedData?.deleteFavoriteList?.id
              ),
            },
          }

          return updatedData
        },
        {
          rollbackOnError: true,
          revalidate: false,
        }
      )
    },
    [deleteFavoriteListMutation, favoritesData?.favorites?.productLists, mutate]
  )

  if (error) {
    throw error
  }

  return {
    loading,
    error,
    favoritesLists: favoritesData?.favorites?.productLists ?? [],
    updateFavorites,
    deleteFavoriteList,
    notifyFavoriteClick,
    refetchFavorites: mutate,
  }
}
